import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { TreatmentRecordView } from './TreatmentRecordView';
import { TreatmentRecordQuickToolbarHost } from './TreatmentRecordQuickToolbarHost';
import {
  TimelineFormDrawer,
  TimelineInvoiceDrawer,
  TimelineFileDrawer,
} from '../../../shell/SidebarTimelineDrawers';
import { TreatmentChartingFilesDrawer } from './TreatmentChartingFilesDrawer';
import { useAuth } from '@helios/auth-firebase-web';
import { NavIcon, useBrandTokens, SidebarToggleButton } from '../../../shell/shared';
import { usePatientShellContext } from '../../../shell/PatientShellLayout';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qEncountersByPatient,
  qEncounterSummaryCounts,
  qEncounterVitalsSnapshot,
  qEncounterTopFiles,
  qEncounterLatestInvoice,
  qVitalsByEncounterId,
  qNoOp,
  type PatientEncounterRow,
  type EncounterSummaryCountRow,
  type EncounterVitalsSnapshotRow,
  type EncounterFilePreviewRow,
  type EncounterLatestInvoiceRow,
  type PatientVitalEntryWithDefinitionRow,
  qEncounterNotesPreviews,
  qEncounterVitalsDetails,
  qEncounterFilesDetails,
  qEncounterFormsPreviews,
  qEncounterInvoicesPreviews,
  qEncounterInvoiceItems,
  type EncounterNotePreviewRow,
  type EncounterVitalsDetailRow,
  type EncounterFileDetailRow,
  type EncounterFormPreviewRow,
  type EncounterInvoicePreviewRow,
  type EncounterInvoiceItemRow,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientWorkspaceSettingsRow,
  qPatientVisualCharts,
  type EncounterVisualChartRow,
  qOdontogramChartsByPatient,
  type OdontogramChartRow,
} from '@helios/data-access';
import { ensureVisitThenCreateEncounter } from '../../../services/encounter-service';
import type { PatientOutletContext } from '../../../shell/PatientShellLayout';
import { formatEncounterTime, type EncounterStatus } from './mockEncountersData';
import {
  PREVIEW_LIMIT,
  PREVIEW_LIMIT_FOCUSED,
  SectionLabel,
  MoreLink,
  EmbeddedFormRow,
  FileThumbnail,
  DocumentFileRow,
  VitalChip,
  InvoicePreviewRow,
  NotePreview,
  DrawingPreviewRow,
  ToothChartPreviewRow,
  isImageFile,
} from './timeline-previews';

// ─── Timeline item types ───

interface EncounterTimelineItem {
  kind: 'encounter';
  id: string;
  sortDate: string;
  encounter: PatientEncounterRow;
}

interface SystemTimelineItem {
  kind: 'system';
  id: string;
  sortDate: string;
  label: string;
  subtitle: string;
  icon: string;
  patientNumber: string | null;
}

type TimelineItem = EncounterTimelineItem | SystemTimelineItem;

// ─── Badges ───

const STATUS_STYLES: Record<EncounterStatus, { bg: string; color: string }> = {
  Open: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  Closed: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)' },
  'In Progress': { bg: 'var(--color-info-bg)', color: 'var(--color-info)' },
};

function StatusBadge({ status }: { status: string }) {
  const key = status === 'open' ? 'Open' : status === 'closed' ? 'Closed' : 'In Progress';
  const s = STATUS_STYLES[key];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '18px',
      }}
    >
      {key}
    </span>
  );
}

const TYPE_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  consultation: { bg: '#FFF7ED', color: '#C2410C', border: '#FDBA74' },
  follow_up: { bg: '#EFF6FF', color: '#1D4ED8', border: '#93C5FD' },
  procedure: { bg: '#F0FDF4', color: '#166534', border: '#86EFAC' },
  triage: { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  follow_up: 'Follow-up',
  procedure: 'Procedure',
  triage: 'Triage',
};

function TypeBadge({ type }: { type: string }) {
  const c = TYPE_COLORS[type] ?? {
    bg: 'var(--color-neutral-bg)',
    color: 'var(--text-muted)',
    border: 'var(--border)',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '18px',
      }}
    >
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

// Preview primitives / helpers are shared with the sidebar timeline.
// See ./timeline-previews for the implementations.

// ─── Helpers ───

function formatDayHeader(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDayKey(iso: string | null): string {
  if (!iso) return 'unknown';
  return new Date(iso).toDateString();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatVitalVal(row: PatientVitalEntryWithDefinitionRow): string {
  if (row.value_number != null) return `${row.value_number}${row.unit ? ` ${row.unit}` : ''}`;
  if (row.value_text) return row.value_text;
  return '--';
}

// ─── Types ───

type StatusFilter = 'all' | 'open' | 'closed';
type DateRange = 'all' | '7' | '30' | '90';
type ActivityFilter = 'all' | 'vitals' | 'notes' | 'files' | 'forms' | 'invoices';

// ─── Peek Panel ───

function EncounterPeekPanel({
  encounter,
  counts,
  vitalsSnap,
  patientId,
  primary,
}: {
  encounter: PatientEncounterRow;
  counts: EncounterSummaryCountRow | undefined;
  vitalsSnap: Record<string, { value: number | null; unit: string | null }> | undefined;
  patientId: string;
  primary: string;
}) {
  const navigate = useNavigate();
  const encId = encounter.id;

  // Peek-specific queries
  const { data: topFiles = [] } = usePowerSyncQuery<EncounterFilePreviewRow>(
    qEncounterTopFiles(encId),
  );
  const { data: latestInvoiceRows = [] } = usePowerSyncQuery<EncounterLatestInvoiceRow>(
    qEncounterLatestInvoice(encId),
  );
  const { data: vitalEntries = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    counts && counts.vitals_count > 0 ? qVitalsByEncounterId(encId) : qNoOp('peek-vitals'),
  );
  const latestInvoice = latestInvoiceRows[0] ?? null;

  // Latest vitals (first session only)
  const latestVitals = useMemo(() => {
    if (vitalEntries.length === 0) return [];
    const firstTs = vitalEntries[0].recorded_at;
    return vitalEntries.filter((v) => v.recorded_at === firstTs).slice(0, 5);
  }, [vitalEntries]);

  const hasActivity =
    counts &&
    (counts.vitals_count > 0 ||
      counts.files_count > 0 ||
      counts.invoices_count > 0 ||
      counts.forms_count > 0 ||
      counts.notes_count > 0);

  const linkStyle = {
    fontSize: 'var(--font-size-xs)',
    color: primary,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 'var(--font-weight-medium)' as const,
    textDecoration: 'none' as const,
  };

  return (
    <div
      style={{
        width: 340,
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg-surface)',
      }}
    >
      {/* Header */}
      <div
        style={{ padding: 'var(--sp-5)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <TypeBadge type={encounter.type ?? 'consultation'} />
          <StatusBadge status={encounter.status ?? 'open'} />
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {formatDateTime(encounter.started_at)}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
          {encounter.provider_name ?? 'Unassigned'}
        </div>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 8, marginTop: 'var(--sp-4)' }}>
          <button
            onClick={() => navigate(`/patient/${patientId}/encounters/${encId}`)}
            style={{
              flex: 1,
              height: 34,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
            }}
          >
            Open Encounter
          </button>
        </div>

        {/* Quick links */}
        <div style={{ display: 'flex', gap: 'var(--sp-4)', marginTop: 'var(--sp-3)' }}>
          <button
            onClick={() => navigate(`/patient/${patientId}/encounters/${encId}?tab=vitals`)}
            style={linkStyle}
          >
            Vitals
          </button>
          <button
            onClick={() => navigate(`/patient/${patientId}/encounters/${encId}?tab=files`)}
            style={linkStyle}
          >
            Documents
          </button>
          <button
            onClick={() => navigate(`/patient/${patientId}/encounters/${encId}?tab=invoices`)}
            style={linkStyle}
          >
            Billing
          </button>
          <button
            onClick={() => navigate(`/patient/${patientId}/encounters/${encId}?tab=forms`)}
            style={linkStyle}
          >
            Forms
          </button>
        </div>
      </div>

      {/* Sections */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-4)' }}>
        {!hasActivity ? (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--sp-8) var(--sp-4)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <p style={{ margin: '0 0 var(--sp-3)' }}>
              No activity recorded for this encounter yet.
            </p>
            <button
              onClick={() => navigate(`/patient/${patientId}/encounters/${encId}`)}
              style={{
                height: 32,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${primary}`,
                background: 'transparent',
                color: primary,
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              Open Encounter
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            {/* Vitals */}
            {counts && counts.vitals_count > 0 && (
              <PeekSection title="Vitals" count={counts.vitals_count} icon="activity">
                {vitalsSnap && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text)',
                      fontWeight: 'var(--font-weight-medium)',
                      marginBottom: 6,
                    }}
                  >
                    {[
                      vitalsSnap.systolic?.value != null && vitalsSnap.diastolic?.value != null
                        ? `BP ${vitalsSnap.systolic.value}/${vitalsSnap.diastolic.value}`
                        : null,
                      vitalsSnap.pulse?.value != null ? `HR ${vitalsSnap.pulse.value}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Vitals recorded'}
                  </div>
                )}
                {latestVitals.map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 'var(--font-size-xs)',
                      padding: '2px 0',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span>{v.name}</span>
                    <span style={{ color: 'var(--text)', fontWeight: 'var(--font-weight-medium)' }}>
                      {formatVitalVal(v)}
                    </span>
                  </div>
                ))}
              </PeekSection>
            )}

            {/* Files */}
            {counts && counts.files_count > 0 && (
              <PeekSection title="Documents" count={counts.files_count} icon="folder">
                {topFiles.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      padding: '2px 0',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span
                      style={{
                        color: 'var(--text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '70%',
                      }}
                    >
                      {f.filename}
                    </span>
                    {f.tag && (
                      <span
                        style={{
                          fontSize: 10,
                          padding: '0 4px',
                          borderRadius: 'var(--radius-xs)',
                          background: 'var(--bg-surface-2)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {f.tag}
                      </span>
                    )}
                  </div>
                ))}
              </PeekSection>
            )}

            {/* Forms */}
            {counts && counts.forms_count > 0 && (
              <PeekSection title="Forms" count={counts.forms_count} icon="file-text">
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {counts.forms_count} form{counts.forms_count > 1 ? 's' : ''} attached
                </div>
              </PeekSection>
            )}

            {/* Invoices */}
            {counts && counts.invoices_count > 0 && (
              <PeekSection title="Billing" count={counts.invoices_count} icon="credit-card">
                {latestInvoice ? (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span style={{ color: 'var(--text)' }}>
                      {latestInvoice.invoice_no ?? 'Invoice'}
                    </span>
                    <span>{latestInvoice.status ?? '—'}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {counts.invoices_count} invoice{counts.invoices_count > 1 ? 's' : ''}
                  </div>
                )}
              </PeekSection>
            )}

            {/* Notes */}
            {counts && counts.notes_count > 0 && (
              <PeekSection title="Notes" count={counts.notes_count} icon="edit">
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {counts.notes_count} note{counts.notes_count > 1 ? 's' : ''}
                </div>
              </PeekSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PeekSection({
  title,
  count,
  icon,
  children,
}: {
  title: string;
  count: number;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-3)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          <NavIcon name={icon} /> {title}
        </span>
        <span style={{ fontSize: 10, color: 'var(--text-placeholder)' }}>{count}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Patient Overview ───

function fmtBal(amount: number): string {
  return `₱${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const COL_STYLE: React.CSSProperties = {
  flex: '1 1 0',
  minWidth: 200,
  borderTop: '3px solid var(--border)',
  padding: 'var(--sp-3) 0 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--color-primary)',
  margin: 0,
};

const ROW_LABEL: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
};

const ROW_VALUE: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  fontWeight: 500,
};

function PatientOverview({
  ctx,
  collapsed: overviewCollapsed,
  onToggle,
}: {
  ctx: PatientOutletContext;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { allergyItems, latestVitals, alertNotes, recentTreatmentRecords, outstandingBalance } =
    ctx;

  const totalCharged =
    recentTreatmentRecords.reduce((s, r) => s + (r.amount_charged_cents ?? 0), 0) / 100;
  const totalPaid =
    recentTreatmentRecords.reduce((s, r) => s + (r.amount_paid_cents ?? 0), 0) / 100;
  const totalBalance = totalCharged - totalPaid;

  return (
    <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border)' }}>
      {/* Header */}
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          width: '100%',
          padding: 'var(--sp-2) var(--sp-5)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--text)',
          textAlign: 'left',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: overviewCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 150ms',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
        Patient Overview
      </button>

      {!overviewCollapsed && (
        <div
          style={{
            padding: '0 var(--sp-5) var(--sp-4)',
            display: 'flex',
            gap: 'var(--sp-5)',
            flexWrap: 'wrap',
          }}
        >
          {/* Column 1: Allergies & Alerts */}
          <div style={{ ...COL_STYLE, borderTopColor: 'var(--color-warning-text)' }}>
            <h4 style={SECTION_TITLE}>Allergies</h4>
            {allergyItems.length === 0 ? (
              <span style={ROW_LABEL}>No known allergies</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                {allergyItems.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <span style={ROW_VALUE}>{a.name}</span>
                    {a.severity && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color:
                            a.severity === 'severe'
                              ? 'var(--color-warning-text)'
                              : 'var(--text-muted)',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {a.severity}
                        {a.reaction ? ` · ${a.reaction}` : ''}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {alertNotes.length > 0 && (
              <>
                <h4
                  style={{
                    ...SECTION_TITLE,
                    color: 'var(--color-warning-text)',
                    marginTop: 'var(--sp-2)',
                  }}
                >
                  Alerts ({alertNotes.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                  {alertNotes.slice(0, 3).map((n) => (
                    <span
                      key={n.id}
                      style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}
                    >
                      {n.title || n.body?.slice(0, 80)}
                      {!n.title && (n.body?.length ?? 0) > 80 ? '…' : ''}
                    </span>
                  ))}
                  {alertNotes.length > 3 && (
                    <span style={ROW_LABEL}>+{alertNotes.length - 3} more</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Column 2: Last Vitals */}
          <div style={{ ...COL_STYLE, borderTopColor: 'var(--color-primary)' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
            >
              <h4 style={SECTION_TITLE}>Last Vitals</h4>
              {latestVitals && (
                <span style={ROW_LABEL}>
                  {new Date(latestVitals.recordedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
            {!latestVitals ? (
              <span style={ROW_LABEL}>No vitals recorded</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                {latestVitals.primary.map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <span style={ROW_LABEL}>{v.name}</span>
                    <span style={ROW_VALUE}>
                      {v.value_number != null ? v.value_number : (v.value_text ?? '—')}
                      {v.unit ? ` ${v.unit}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Column 3: Billing & Treatment */}
          <div style={{ ...COL_STYLE, borderTopColor: 'var(--color-success-text)' }}>
            <h4 style={SECTION_TITLE}>Billing</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <span style={ROW_LABEL}>Total Charged</span>
                <span style={ROW_VALUE}>{fmtBal(totalCharged)}</span>
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <span style={ROW_LABEL}>Total Paid</span>
                <span style={{ ...ROW_VALUE, color: 'var(--color-success-text)' }}>
                  {fmtBal(totalPaid)}
                </span>
              </div>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <span style={ROW_LABEL}>Balance</span>
                <span
                  style={{
                    ...ROW_VALUE,
                    color: totalBalance > 0 ? 'var(--color-warning-text)' : 'var(--text)',
                  }}
                >
                  {fmtBal(totalBalance)}
                </span>
              </div>
              {outstandingBalance && outstandingBalance.unpaid_count > 0 && (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-warning-text)',
                    marginTop: 2,
                  }}
                >
                  {outstandingBalance.unpaid_count} unpaid invoice
                  {outstandingBalance.unpaid_count > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {recentTreatmentRecords.length > 0 && (
              <>
                <h4 style={{ ...SECTION_TITLE, marginTop: 'var(--sp-2)' }}>Recent Treatment</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                  {recentTreatmentRecords.slice(0, 3).map((r) => (
                    <div
                      key={r.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>
                        {r.procedure_name}
                      </span>
                      <span style={ROW_LABEL}>
                        {r.date
                          ? new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })
                          : '—'}
                      </span>
                    </div>
                  ))}
                  {recentTreatmentRecords.length > 3 && (
                    <span style={ROW_LABEL}>+{recentTreatmentRecords.length - 3} more</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───

const PAGE_SIZE = 50;

export function EncountersListPage() {
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const ctx = usePatientShellContext();
  const { collapsed, onToggleSidebar } = ctx;

  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = (searchParams.get('view') === 'treatment' ? 'treatment' : 'encounters') as
    | 'encounters'
    | 'treatment';
  const setViewMode = (mode: 'encounters' | 'treatment') => {
    setSearchParams(mode === 'treatment' ? { view: 'treatment' } : {}, { replace: true });
  };

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [overviewCollapsed, setOverviewCollapsed] = useState(false);
  const addRecordRef = useRef<(() => void) | null>(null);

  // Treatment Record drill-in drawer state (PRD.182 + PRD.180): in
  // Treatment Record mode, clicking Forms / Invoices / Files on the
  // timeline opens a contextual right drawer instead of navigating away.
  const [drillFormDrawer, setDrillFormDrawer] = useState<{
    formInstanceId: string;
    encounterId: string;
  } | null>(null);
  const [drillInvoiceDrawer, setDrillInvoiceDrawer] = useState<{
    invoiceId: string;
    encounterId: string;
  } | null>(null);
  const [drillFileDrawer, setDrillFileDrawer] = useState<{
    fileId: string;
    encounterId: string;
  } | null>(null);
  const [drillChartDrawer, setDrillChartDrawer] = useState<{
    encounterId: string;
    tab: 'tooth-chart' | 'draw' | 'before-after';
    chartId: string | null;
  } | null>(null);

  // ── Data ──
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId ?? ''),
  );
  const { data: summaryCounts = [] } = usePowerSyncQuery<EncounterSummaryCountRow>(
    qEncounterSummaryCounts(patientId ?? ''),
  );
  const { data: vitalsSnapshot = [] } = usePowerSyncQuery<EncounterVitalsSnapshotRow>(
    qEncounterVitalsSnapshot(patientId ?? ''),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId ?? ''),
  );
  const { data: vitalsDetails = [] } = usePowerSyncQuery<EncounterVitalsDetailRow>(
    qEncounterVitalsDetails(patientId ?? ''),
  );
  const { data: filesDetails = [] } = usePowerSyncQuery<EncounterFileDetailRow>(
    qEncounterFilesDetails(patientId ?? ''),
  );
  const { data: formsPreviews = [] } = usePowerSyncQuery<EncounterFormPreviewRow>(
    qEncounterFormsPreviews(patientId ?? ''),
  );
  const { data: invoicesPreviews = [] } = usePowerSyncQuery<EncounterInvoicePreviewRow>(
    qEncounterInvoicesPreviews(patientId ?? ''),
  );
  const { data: invoiceItems = [] } = usePowerSyncQuery<EncounterInvoiceItemRow>(
    qEncounterInvoiceItems(patientId ?? ''),
  );
  const { data: visualCharts = [] } = usePowerSyncQuery<EncounterVisualChartRow>(
    qPatientVisualCharts(patientId ?? ''),
  );
  const { data: odontogramCharts = [] } = usePowerSyncQuery<OdontogramChartRow>(
    qOdontogramChartsByPatient(patientId ?? ''),
  );
  // Workspace mode (PRD.182). In Treatment Record mode the Visit Note is
  // hidden as a user-facing concept: timeline cards aren't navigable to
  // the encounter detail page and the "New Visit Note" CTA is hidden in
  // favor of the shared quick toolbar.
  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const workspaceUI =
    workspaceSettingsRows.length > 0
      ? mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0])
      : null;
  const workspaceMode = workspaceUI?.workspaceMode ?? 'full_workspace';
  const treatmentRecordDrawerWidth = workspaceUI?.treatmentRecordDrawerWidth ?? 'standard';
  const isTreatmentRecordMode = workspaceMode === 'treatment_record';
  const patient = ctx.patient;

  // ── Derived maps ──
  const countsMap = useMemo(() => {
    const map: Record<string, EncounterSummaryCountRow> = {};
    for (const r of summaryCounts) map[r.encounter_id] = r;
    return map;
  }, [summaryCounts]);

  const vitalsMap = useMemo(() => {
    const map: Record<string, Record<string, { value: number | null; unit: string | null }>> = {};
    for (const r of vitalsSnapshot) {
      if (!map[r.encounter_id]) map[r.encounter_id] = {};
      map[r.encounter_id][r.code] = { value: r.value_number, unit: r.unit };
    }
    return map;
  }, [vitalsSnapshot]);

  const vitalsDetailMap = useMemo(() => {
    const map: Record<string, EncounterVitalsDetailRow[]> = {};
    for (const r of vitalsDetails) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [vitalsDetails]);

  const filesDetailMap = useMemo(() => {
    const map: Record<string, EncounterFileDetailRow[]> = {};
    for (const r of filesDetails) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [filesDetails]);

  const notesMap = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const r of notesPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [notesPreviews]);

  const formsMap = useMemo(() => {
    const map: Record<string, EncounterFormPreviewRow[]> = {};
    for (const r of formsPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [formsPreviews]);

  const invoicesMap = useMemo(() => {
    const map: Record<string, EncounterInvoicePreviewRow[]> = {};
    for (const r of invoicesPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      map[r.encounter_id].push(r);
    }
    return map;
  }, [invoicesPreviews]);

  const invoiceItemsByInvoice = useMemo(() => {
    const map: Record<string, EncounterInvoiceItemRow[]> = {};
    for (const r of invoiceItems) {
      if (!map[r.invoice_id]) map[r.invoice_id] = [];
      map[r.invoice_id].push(r);
    }
    return map;
  }, [invoiceItems]);

  const chartsByEncounter = useMemo(() => {
    const map: Record<string, EncounterVisualChartRow[]> = {};
    for (const c of visualCharts) {
      if (!map[c.encounter_id]) map[c.encounter_id] = [];
      map[c.encounter_id].push(c);
    }
    return map;
  }, [visualCharts]);

  const toothChartsByEncounter = useMemo(() => {
    const map: Record<string, OdontogramChartRow[]> = {};
    for (const c of odontogramCharts) {
      if (!c.encounter_id) continue;
      if (!map[c.encounter_id]) map[c.encounter_id] = [];
      map[c.encounter_id].push(c);
    }
    return map;
  }, [odontogramCharts]);

  // Lookup map for resolving before/after and drawing base-image file URLs
  // when rendering chart thumbnails.
  const filesById = useMemo(() => {
    const map = new Map<string, { url: string | null }>();
    for (const f of filesDetails) map.set(f.id, { url: f.url });
    return map;
  }, [filesDetails]);

  // ── Build unified timeline ──
  const timelineItems = useMemo<TimelineItem[]>(() => {
    // Encounter items
    let encItems: EncounterTimelineItem[] = encounterRows.map((e) => ({
      kind: 'encounter',
      id: e.id,
      sortDate: e.started_at ?? e.created_at ?? '',
      encounter: e,
    }));

    // Apply filters to encounters
    if (statusFilter !== 'all') {
      encItems = encItems.filter((i) => i.encounter.status === statusFilter);
    }
    if (dateRange !== 'all') {
      const days = Number(dateRange);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      encItems = encItems.filter((i) => i.sortDate && new Date(i.sortDate) >= cutoff);
    }
    if (activityFilter !== 'all') {
      encItems = encItems.filter((i) => {
        const c = countsMap[i.id];
        if (!c) return false;
        switch (activityFilter) {
          case 'vitals':
            return c.vitals_count > 0;
          case 'notes':
            return c.notes_count > 0;
          case 'files':
            return c.files_count > 0;
          case 'forms':
            return c.forms_count > 0;
          case 'invoices':
            return c.invoices_count > 0;
          default:
            return true;
        }
      });
    }
    const items: TimelineItem[] = [...encItems];

    // System item: Patient Registered
    if (patient) {
      const regDate = patient.created_at ?? patient.updated_at ?? '';
      const matchesStatus = statusFilter === 'all'; // system items only show in "all" status
      let matchesDate = true;
      if (dateRange !== 'all' && regDate) {
        const days = Number(dateRange);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        matchesDate = new Date(regDate) >= cutoff;
      }
      if (matchesStatus && matchesDate) {
        items.push({
          kind: 'system',
          id: '__patient_registered__',
          sortDate: regDate,
          label: 'Patient Registered',
          subtitle: 'Patient record created',
          icon: 'user-plus',
          patientNumber: patient.patient_number,
        });
      }
    }

    // Sort DESC by date
    items.sort((a, b) => {
      if (!a.sortDate && !b.sortDate) return 0;
      if (!a.sortDate) return 1;
      if (!b.sortDate) return -1;
      return new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime();
    });

    return items;
  }, [encounterRows, patient, statusFilter, dateRange, activityFilter, countsMap]);

  const visible = useMemo(
    () => timelineItems.slice(0, visibleCount),
    [timelineItems, visibleCount],
  );
  const hasMore = visible.length < timelineItems.length;

  // ── Selection ──
  const selectedItem = useMemo(
    () => (selectedId ? (timelineItems.find((i) => i.id === selectedId) ?? null) : null),
    [selectedId, timelineItems],
  );

  const handleNewEncounter = useCallback(async () => {
    if (!patientId || !orgId || !db) return;
    const { encounterId } = await ensureVisitThenCreateEncounter({ db, orgId, patientId });
    navigate(`/patient/${patientId}/encounters/${encounterId}`);
  }, [db, orgId, patientId, navigate]);

  const filterBtnStyle = (active: boolean) => ({
    height: 28,
    padding: '0 10px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)' as const,
    border: active ? `1px solid ${primary}` : '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: active ? primary : 'var(--bg-surface)',
    color: active ? '#fff' : 'var(--text-muted)',
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Page title: Patient Overview */}
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-5) 0',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          flexShrink: 0,
        }}
      >
        {onToggleSidebar && (
          <SidebarToggleButton collapsed={collapsed} onToggle={onToggleSidebar} />
        )}
        <h1
          style={{
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Patient Overview
        </h1>
      </div>

      {/* Patient Overview */}
      <PatientOverview
        ctx={ctx}
        collapsed={overviewCollapsed}
        onToggle={() => setOverviewCollapsed((v) => !v)}
      />

      {/* Section header: view toggle + actions */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            background: 'var(--bg-surface-2)',
            borderRadius: 'var(--radius-lg)',
            padding: 3,
            gap: 3,
          }}
        >
          {(['encounters', 'treatment'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                height: 36,
                padding: '0 20px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: viewMode === mode ? 'var(--bg-surface)' : 'transparent',
                boxShadow: viewMode === mode ? 'var(--shadow-sm)' : 'none',
                fontSize: 'var(--font-size-sm)',
                fontWeight:
                  viewMode === mode ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                color: viewMode === mode ? 'var(--text)' : 'var(--text-muted)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {mode === 'encounters'
                ? isTreatmentRecordMode
                  ? 'Patient Timeline'
                  : 'Visit Notes'
                : 'Treatment Record'}
            </button>
          ))}
        </div>
        {/* In Treatment Record mode the "New Visit Note" CTA is hidden in
            favor of the shared quick toolbar (PRD.182 §5.5). The "Add Record"
            CTA on the Treatment Record view still shows. */}
        {!(isTreatmentRecordMode && viewMode === 'encounters') && (
          <button
            onClick={
              viewMode === 'encounters' ? handleNewEncounter : () => addRecordRef.current?.()
            }
            style={{
              background: primary,
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '0 16px',
              height: 34,
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexShrink: 0,
            }}
          >
            <NavIcon name="plus" /> {viewMode === 'encounters' ? 'New Visit Note' : 'Add Record'}
          </button>
        )}
      </div>

      {/* Treatment Record View */}
      {viewMode === 'treatment' && (
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-5)' }}>
          <TreatmentRecordView patientId={patientId ?? ''} addRecordRef={addRecordRef} />
        </div>
      )}

      {/* Encounters View — Filters */}
      {viewMode === 'encounters' && (
        <>
          {/* PRD.182 — shared Treatment Record Quick Toolbar */}
          <div style={{ padding: '0 var(--sp-5) var(--sp-3)', flexShrink: 0 }}>
            <TreatmentRecordQuickToolbarHost patientId={patientId ?? ''} />
          </div>
          <div
            style={{
              padding: '0 var(--sp-5) var(--sp-3)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            {/* Status filter */}
            <div style={{ display: 'flex', gap: 0 }}>
              {(
                [
                  ['all', 'All'],
                  ['open', 'Open'],
                  ['closed', 'Closed'],
                ] as const
              ).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  style={{
                    ...filterBtnStyle(statusFilter === val),
                    borderRadius:
                      val === 'all'
                        ? 'var(--radius-sm) 0 0 var(--radius-sm)'
                        : val === 'closed'
                          ? '0 var(--radius-sm) var(--radius-sm) 0'
                          : '0',
                    marginLeft: val !== 'all' ? -1 : 0,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Date range filter */}
            <div style={{ display: 'flex', gap: 0 }}>
              {(
                [
                  ['all', 'All'],
                  ['7', '7d'],
                  ['30', '30d'],
                  ['90', '90d'],
                ] as const
              ).map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setDateRange(val)}
                  style={{
                    ...filterBtnStyle(dateRange === val),
                    borderRadius:
                      val === 'all'
                        ? 'var(--radius-sm) 0 0 var(--radius-sm)'
                        : val === '90'
                          ? '0 var(--radius-sm) var(--radius-sm) 0'
                          : '0',
                    marginLeft: val !== 'all' ? -1 : 0,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Activity filter */}
            <div style={{ display: 'flex', gap: 0 }}>
              {(
                [
                  ['all', 'All'],
                  ['vitals', 'Vitals'],
                  ['notes', 'Notes'],
                  ['files', 'Files'],
                  ['forms', 'Forms'],
                  ['invoices', 'Invoices'],
                ] as const
              ).map(([val, label], i, arr) => (
                <button
                  key={val}
                  onClick={() => setActivityFilter(val)}
                  style={{
                    ...filterBtnStyle(activityFilter === val),
                    borderRadius:
                      i === 0
                        ? 'var(--radius-sm) 0 0 var(--radius-sm)'
                        : i === arr.length - 1
                          ? '0 var(--radius-sm) var(--radius-sm) 0'
                          : '0',
                    marginLeft: i > 0 ? -1 : 0,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Body: timeline + peek panel */}
          <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Timeline */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'auto',
                padding: '0 var(--sp-5) var(--sp-5)',
              }}
            >
              {timelineItems.length === 0 ? (
                <div
                  style={{
                    padding: 40,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  No encounters found
                </div>
              ) : (
                <div style={{ position: 'relative', paddingLeft: 28 }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 7,
                      top: 8,
                      bottom: 8,
                      width: 2,
                      background: 'var(--border)',
                    }}
                  />

                  {visible.map((item, idx) => {
                    const prevDay = idx > 0 ? getDayKey(visible[idx - 1].sortDate || null) : null;
                    const curDay = getDayKey(item.sortDate || null);
                    const showDayHeader = curDay !== prevDay;
                    const isSelected = selectedId === item.id;

                    return (
                      <div key={item.id}>
                        {showDayHeader && (
                          <div
                            style={{
                              position: 'relative',
                              padding: idx === 0 ? '0 0 var(--sp-3)' : 'var(--sp-4) 0 var(--sp-3)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 'var(--font-weight-semi)',
                              color: 'var(--text-muted)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            {item.sortDate ? formatDayHeader(item.sortDate) : 'Unknown'}
                          </div>
                        )}

                        {item.kind === 'system' ? (
                          /* ── System item (Patient Registered) ── */
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedId(item.id)}
                            onKeyDown={(e) => {
                              if (e.key === ' ') {
                                e.preventDefault();
                                setSelectedId(item.id);
                              }
                            }}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--sp-3)',
                              padding: 'var(--sp-3) var(--sp-4)',
                              marginBottom: 'var(--sp-2)',
                              background: isSelected ? '#f0fdf4' : 'var(--bg-surface-2, #f9fafb)',
                              border: isSelected ? '2px solid #86efac' : '1px dashed var(--border)',
                              borderRadius: 'var(--radius-lg)',
                              cursor: 'pointer',
                              outline: 'none',
                              transition: 'border-color 0.15s',
                            }}
                          >
                            {/* Marker — diamond shape */}
                            <div
                              style={{
                                position: 'absolute',
                                left: isSelected ? -25 : -24,
                                top: '50%',
                                transform: 'translateY(-50%) rotate(45deg)',
                                width: 10,
                                height: 10,
                                borderRadius: 2,
                                background: '#86efac',
                                border: '2px solid var(--bg-surface)',
                              }}
                            />

                            <div
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 'var(--radius-md)',
                                background: '#dcfce7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#16a34a',
                                flexShrink: 0,
                              }}
                            >
                              <NavIcon name={item.icon} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 'var(--font-size-sm)',
                                  fontWeight: 'var(--font-weight-semi)',
                                  color: 'var(--text)',
                                }}
                              >
                                {item.label}
                              </div>
                              <div
                                style={{
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--text-muted)',
                                }}
                              >
                                {item.subtitle}
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text-placeholder)',
                                flexShrink: 0,
                              }}
                            >
                              {item.sortDate ? formatDateTime(item.sortDate) : ''}
                            </div>
                          </div>
                        ) : (
                          /* ── Encounter item ── */
                          (() => {
                            const enc = item.encounter;
                            const isOpen = enc.status === 'open';
                            const forms = formsMap[enc.id] ?? [];
                            const notes = notesMap[enc.id] ?? [];
                            const files = filesDetailMap[enc.id] ?? [];
                            const invoices = invoicesMap[enc.id] ?? [];
                            const charts = chartsByEncounter[enc.id] ?? [];
                            const toothCharts = toothChartsByEncounter[enc.id] ?? [];
                            return (
                              <div
                                role={isTreatmentRecordMode ? undefined : 'button'}
                                tabIndex={isTreatmentRecordMode ? undefined : 0}
                                onClick={
                                  isTreatmentRecordMode
                                    ? undefined
                                    : () => navigate(`/patient/${patientId}/encounters/${enc.id}`)
                                }
                                onKeyDown={
                                  isTreatmentRecordMode
                                    ? undefined
                                    : (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          navigate(`/patient/${patientId}/encounters/${enc.id}`);
                                        }
                                      }
                                }
                                style={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'stretch',
                                  gap: 'var(--sp-4)',
                                  padding: 'var(--sp-4)',
                                  marginBottom: 'var(--sp-2)',
                                  background: isSelected ? `${primary}08` : 'var(--bg-surface)',
                                  border: isSelected
                                    ? `2px solid ${primary}`
                                    : '1px solid var(--border)',
                                  borderRadius: 'var(--radius-lg)',
                                  cursor: isTreatmentRecordMode ? 'default' : 'pointer',
                                  transition: 'box-shadow 0.15s, border-color 0.15s',
                                  outline: 'none',
                                }}
                                onMouseEnter={
                                  isTreatmentRecordMode
                                    ? undefined
                                    : (e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.boxShadow =
                                            '0 2px 8px rgba(0,0,0,0.06)';
                                          e.currentTarget.style.borderColor = primary;
                                        }
                                      }
                                }
                                onMouseLeave={
                                  isTreatmentRecordMode
                                    ? undefined
                                    : (e) => {
                                        if (!isSelected) {
                                          e.currentTarget.style.boxShadow = '';
                                          e.currentTarget.style.borderColor = 'var(--border)';
                                        }
                                      }
                                }
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: isSelected ? -25 : -24,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    background: isOpen ? primary : 'var(--text-placeholder)',
                                    border: '2px solid var(--bg-surface)',
                                    boxShadow: isOpen ? `0 0 0 3px ${primary}22` : undefined,
                                  }}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6,
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TypeBadge type={enc.type ?? 'consultation'} />
                                    <StatusBadge status={enc.status ?? 'open'} />
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--sp-3)',
                                      fontSize: 'var(--font-size-sm)',
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: 'var(--text)',
                                        fontWeight: 'var(--font-weight-medium)',
                                      }}
                                    >
                                      {enc.started_at ? formatEncounterTime(enc.started_at) : '—'}
                                    </span>
                                    {enc.provider_name && (
                                      <>
                                        <span style={{ color: 'var(--text-placeholder)' }}>·</span>
                                        <span style={{ color: 'var(--text-muted)' }}>
                                          {enc.provider_name}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  {/* Main content body: actual preview sections,
                                      focused on the active content filter. */}
                                  {(() => {
                                    const isAll = activityFilter === 'all';
                                    const showForms = isAll || activityFilter === 'forms';
                                    const showNotes = isAll || activityFilter === 'notes';
                                    const showFiles = isAll || activityFilter === 'files';
                                    const showInvoices = isAll || activityFilter === 'invoices';
                                    const showVitals = isAll || activityFilter === 'vitals';

                                    const limit = (focused: boolean) =>
                                      focused ? PREVIEW_LIMIT_FOCUSED : PREVIEW_LIMIT;
                                    const formsLimit = limit(activityFilter === 'forms');
                                    const notesLimit = limit(activityFilter === 'notes');
                                    const filesLimit = limit(activityFilter === 'files');
                                    const invoicesLimit = limit(activityFilter === 'invoices');

                                    const allVitals = vitalsDetailMap[enc.id] ?? [];
                                    const vitalsLimit =
                                      activityFilter === 'vitals' ? allVitals.length : 4;
                                    const vitalsVisible = allVitals.slice(0, vitalsLimit);
                                    const vitalsHidden = allVitals.length - vitalsVisible.length;

                                    const anyVisibleContent =
                                      (showForms && forms.length > 0) ||
                                      (showNotes && notes.length > 0) ||
                                      (showFiles && files.length > 0) ||
                                      (showFiles && charts.length > 0) ||
                                      (showFiles && toothCharts.length > 0) ||
                                      (showInvoices && invoices.length > 0) ||
                                      (showVitals && allVitals.length > 0);

                                    if (!anyVisibleContent) {
                                      return (
                                        <div
                                          style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--text-muted)',
                                            fontStyle: 'italic',
                                            marginTop: 2,
                                          }}
                                        >
                                          No activity recorded
                                        </div>
                                      );
                                    }

                                    return (
                                      <div
                                        style={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: 6,
                                          marginTop: 2,
                                        }}
                                      >
                                        {/* Vitals — actual recorded values as compact chips */}
                                        {showVitals && allVitals.length > 0 && (
                                          <>
                                            <SectionLabel label="Vitals" count={allVitals.length} />
                                            <div
                                              style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 6,
                                                alignItems: 'center',
                                              }}
                                            >
                                              {vitalsVisible.map((v, vi) => (
                                                <VitalChip key={vi} vital={v} />
                                              ))}
                                              {vitalsHidden > 0 && (
                                                <span
                                                  style={{
                                                    fontSize: 10,
                                                    color: 'var(--text-placeholder)',
                                                    fontWeight: 'var(--font-weight-medium)',
                                                  }}
                                                >
                                                  +{vitalsHidden} more
                                                </span>
                                              )}
                                            </div>
                                          </>
                                        )}

                                        {/* Forms — embedded mini Forms list */}
                                        {showForms && forms.length > 0 && (
                                          <>
                                            <SectionLabel label="Forms" count={forms.length} />
                                            <div
                                              style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4,
                                              }}
                                            >
                                              {forms.slice(0, formsLimit).map((f) => (
                                                <EmbeddedFormRow
                                                  key={f.id}
                                                  form={f}
                                                  onOpen={() => {
                                                    if (isTreatmentRecordMode) {
                                                      setDrillFormDrawer({
                                                        formInstanceId: f.id,
                                                        encounterId: enc.id,
                                                      });
                                                    } else {
                                                      navigate(
                                                        `/patient/${patientId}/encounters/${enc.id}/forms/${f.id}`,
                                                      );
                                                    }
                                                  }}
                                                />
                                              ))}
                                              {forms.length > formsLimit && (
                                                <span
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                      `/patient/${patientId}/encounters/${enc.id}?tab=forms`,
                                                    );
                                                  }}
                                                  style={{
                                                    fontSize: 10,
                                                    color: primary,
                                                    fontWeight: 'var(--font-weight-medium)',
                                                    cursor: 'pointer',
                                                    padding: '2px 0 0 10px',
                                                  }}
                                                >
                                                  View all {forms.length} forms →
                                                </span>
                                              )}
                                            </div>
                                          </>
                                        )}

                                        {/* Notes — full title + multi-line body so the
                                            summary matches the Notes tab content. */}
                                        {showNotes && notes.length > 0 && (
                                          <>
                                            <SectionLabel label="Notes" count={notes.length} />
                                            {notes.slice(0, notesLimit).map((note, ni) => (
                                              <NotePreview
                                                key={note.created_at ?? `${ni}`}
                                                note={note}
                                              />
                                            ))}
                                            {notes.length > notesLimit && (
                                              <MoreLink
                                                count={notes.length - notesLimit}
                                                singular="note"
                                              />
                                            )}
                                          </>
                                        )}

                                        {/* Files — adaptive: thumbnails for images,
                                            clickable list rows for documents. */}
                                        {showFiles &&
                                          files.length > 0 &&
                                          (() => {
                                            const imageFiles = files.filter(isImageFile);
                                            const docFiles = files.filter((f) => !isImageFile(f));
                                            const imgLimit = activityFilter === 'files' ? 6 : 3;
                                            const docLimit = filesLimit;
                                            const imgVisible = imageFiles.slice(0, imgLimit);
                                            const docVisible = docFiles.slice(0, docLimit);
                                            const hidden =
                                              imageFiles.length -
                                              imgVisible.length +
                                              (docFiles.length - docVisible.length);
                                            const filesTab = `/patient/${patientId}/encounters/${enc.id}?tab=files`;
                                            return (
                                              <>
                                                <SectionLabel label="Files" count={files.length} />
                                                {imgVisible.length > 0 && (
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      flexWrap: 'wrap',
                                                      gap: 6,
                                                    }}
                                                  >
                                                    {imgVisible.map((f) => (
                                                      <FileThumbnail
                                                        key={f.id}
                                                        file={f}
                                                        onOpen={() => {
                                                          if (isTreatmentRecordMode) {
                                                            setDrillFileDrawer({
                                                              fileId: f.id,
                                                              encounterId: enc.id,
                                                            });
                                                          } else if (f.url) {
                                                            window.open(
                                                              f.url,
                                                              '_blank',
                                                              'noopener',
                                                            );
                                                          } else {
                                                            navigate(filesTab);
                                                          }
                                                        }}
                                                      />
                                                    ))}
                                                  </div>
                                                )}
                                                {docVisible.map((f) => (
                                                  <DocumentFileRow
                                                    key={f.id}
                                                    file={f}
                                                    onOpen={() => {
                                                      if (isTreatmentRecordMode) {
                                                        setDrillFileDrawer({
                                                          fileId: f.id,
                                                          encounterId: enc.id,
                                                        });
                                                      } else {
                                                        navigate(filesTab);
                                                      }
                                                    }}
                                                  />
                                                ))}
                                                {hidden > 0 && (
                                                  <MoreLink count={hidden} singular="file" />
                                                )}
                                              </>
                                            );
                                          })()}

                                        {/* Saved Chartings — drawings + before/after pairs
                                            (encounter_visual_charts) and tooth charts
                                            (odontogram_charts). Rendered as thumbnails
                                            alongside file thumbnails. */}
                                        {showFiles &&
                                          (charts.length > 0 || toothCharts.length > 0) &&
                                          (() => {
                                            const totalCharts = charts.length + toothCharts.length;
                                            const chartsThumbLimit =
                                              activityFilter === 'files' ? 6 : 3;
                                            const toothVisible = toothCharts.slice(
                                              0,
                                              chartsThumbLimit,
                                            );
                                            const remainingSlots = Math.max(
                                              0,
                                              chartsThumbLimit - toothVisible.length,
                                            );
                                            const visualVisible = charts.slice(0, remainingSlots);
                                            const hidden =
                                              totalCharts -
                                              toothVisible.length -
                                              visualVisible.length;
                                            return (
                                              <>
                                                <SectionLabel
                                                  label="Saved Chartings"
                                                  count={totalCharts}
                                                />
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 6,
                                                  }}
                                                >
                                                  {toothVisible.map((tc) => (
                                                    <ToothChartPreviewRow
                                                      key={tc.id}
                                                      chart={tc}
                                                      onOpen={() => {
                                                        if (isTreatmentRecordMode) {
                                                          setDrillChartDrawer({
                                                            encounterId: enc.id,
                                                            tab: 'tooth-chart',
                                                            chartId: tc.id,
                                                          });
                                                        } else {
                                                          navigate(
                                                            `/patient/${patientId}/encounters/${enc.id}?tab=charting`,
                                                          );
                                                        }
                                                      }}
                                                    />
                                                  ))}
                                                  {visualVisible.map((c) => (
                                                    <DrawingPreviewRow
                                                      key={c.id}
                                                      chart={c}
                                                      filesById={filesById}
                                                      onOpen={() => {
                                                        if (isTreatmentRecordMode) {
                                                          const isBeforeAfter =
                                                            c.chart_type === 'before_after';
                                                          setDrillChartDrawer({
                                                            encounterId: enc.id,
                                                            tab: isBeforeAfter
                                                              ? 'before-after'
                                                              : 'draw',
                                                            chartId: c.id,
                                                          });
                                                        } else {
                                                          navigate(
                                                            `/patient/${patientId}/encounters/${enc.id}?tab=charting`,
                                                          );
                                                        }
                                                      }}
                                                    />
                                                  ))}
                                                </div>
                                                {hidden > 0 && (
                                                  <MoreLink count={hidden} singular="charting" />
                                                )}
                                              </>
                                            );
                                          })()}

                                        {/* Invoices */}
                                        {showInvoices && invoices.length > 0 && (
                                          <>
                                            <SectionLabel
                                              label="Invoices"
                                              count={invoices.length}
                                            />
                                            {invoices.slice(0, invoicesLimit).map((inv) => (
                                              <InvoicePreviewRow
                                                key={inv.id}
                                                invoice={inv}
                                                items={invoiceItemsByInvoice[inv.id] ?? []}
                                                onOpen={() => {
                                                  if (isTreatmentRecordMode) {
                                                    setDrillInvoiceDrawer({
                                                      invoiceId: inv.id,
                                                      encounterId: enc.id,
                                                    });
                                                  } else {
                                                    navigate(
                                                      `/patient/${patientId}/encounters/${enc.id}?tab=invoices&invoiceId=${inv.id}`,
                                                    );
                                                  }
                                                }}
                                              />
                                            ))}
                                            {invoices.length > invoicesLimit && (
                                              <MoreLink
                                                count={invoices.length - invoicesLimit}
                                                singular="invoice"
                                              />
                                            )}
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                                {!isTreatmentRecordMode && (
                                  <div
                                    style={{
                                      color: 'var(--text-placeholder)',
                                      flexShrink: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <NavIcon name="chevron-right" />
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div style={{ textAlign: 'center', padding: 'var(--sp-4)' }}>
                      <button
                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                        style={{
                          height: 34,
                          padding: '0 20px',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-surface)',
                          color: 'var(--text)',
                          fontSize: 'var(--font-size-sm)',
                          cursor: 'pointer',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                      >
                        Load more ({timelineItems.length - visible.length} remaining)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Peek Panel */}
            {selectedItem && selectedItem.kind === 'encounter' && (
              <EncounterPeekPanel
                encounter={selectedItem.encounter}
                counts={countsMap[selectedItem.encounter.id]}
                vitalsSnap={vitalsMap[selectedItem.encounter.id]}
                patientId={patientId ?? ''}
                primary={primary}
              />
            )}
            {selectedItem && selectedItem.kind === 'system' && (
              <div
                style={{
                  width: 340,
                  flexShrink: 0,
                  borderLeft: '1px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  background: 'var(--bg-surface)',
                }}
              >
                <div style={{ padding: 'var(--sp-5)', borderBottom: '1px solid var(--border)' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 'var(--sp-3)',
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 'var(--radius-md)',
                        background: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#16a34a',
                      }}
                    >
                      <NavIcon name="user-plus" />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        Patient Registered
                      </div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                        Patient record created
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    padding: 'var(--sp-5)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-4)',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginBottom: 4,
                      }}
                    >
                      Patient ID
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text)',
                        fontWeight: 'var(--font-weight-medium)',
                      }}
                    >
                      {selectedItem.patientNumber ?? patientId ?? '—'}
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginBottom: 4,
                      }}
                    >
                      Registered
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text)',
                        fontWeight: 'var(--font-weight-medium)',
                      }}
                    >
                      {selectedItem.sortDate ? formatDateTime(selectedItem.sortDate) : '—'}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/patient/${patientId}`)}
                    style={{
                      height: 34,
                      borderRadius: 'var(--radius-md)',
                      border: `1px solid ${primary}`,
                      background: 'transparent',
                      color: primary,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      cursor: 'pointer',
                      marginTop: 'var(--sp-3)',
                    }}
                  >
                    Open Patient Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Treatment Record drill-in drawers (PRD.182 + PRD.180) */}
      <TimelineFormDrawer
        open={drillFormDrawer != null}
        onOpenChange={(o) => {
          if (!o) setDrillFormDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={drillFormDrawer?.encounterId ?? null}
        formInstanceId={drillFormDrawer?.formInstanceId ?? null}
      />
      <TimelineInvoiceDrawer
        open={drillInvoiceDrawer != null}
        onOpenChange={(o) => {
          if (!o) setDrillInvoiceDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={drillInvoiceDrawer?.encounterId ?? null}
        invoiceId={drillInvoiceDrawer?.invoiceId ?? null}
      />
      <TimelineFileDrawer
        open={drillFileDrawer != null}
        onOpenChange={(o) => {
          if (!o) setDrillFileDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={drillFileDrawer?.encounterId ?? null}
        fileId={drillFileDrawer?.fileId ?? null}
      />
      <TreatmentChartingFilesDrawer
        open={drillChartDrawer != null}
        onOpenChange={(o) => {
          if (!o) setDrillChartDrawer(null);
        }}
        patientId={patientId ?? ''}
        initialEncounterId={drillChartDrawer?.encounterId ?? null}
        // Drill-in is scoped to a specific saved chart for an existing
        // encounter — the date is implicit, so we hide the date chip.
        initialDate={null}
        hideDateChip
        drawerWidth={treatmentRecordDrawerWidth}
        initialTab={drillChartDrawer?.tab}
        initialBeforeAfterChartId={
          drillChartDrawer?.tab === 'before-after' ? (drillChartDrawer?.chartId ?? null) : null
        }
        initialDrawChartId={
          drillChartDrawer?.tab === 'draw' ? (drillChartDrawer?.chartId ?? null) : null
        }
      />
    </div>
  );
}
